import { useCallback, useEffect, useRef, useState } from 'react';
import type { FichaDisputa, ListaDisputas, Prueba } from '../../../api/ops/pagos';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Campo } from '../../../componentes/Campo';
import { Casilla } from '../../../componentes/Casilla';
import { Chip } from '../../../componentes/Chip';
import { EstadoCargando, EstadoError, EstadoSinPermiso } from '../../../componentes/EstadoPagina';
import { Filtros } from '../../../componentes/Filtros';
import { Icono } from '../../../componentes/Icono';
import { Paginacion } from '../../../componentes/Paginacion';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { Ventana } from '../../../componentes/Ventana';
import { rolesDe } from '../../../logica/acceso';
import { dinero } from '../../../logica/dinero';
import { avisoBreveDeError, estadoDeError } from '../../../logica/estados';
import { diaMes, kilobytes } from '../../../logica/fechasCortas';
import { haySiguiente, usePaginacionCursor } from '../../../logica/paginacion';
import { useCabecera } from '../../../marco/cabecera';
import { usePermisos } from '../../../permisos/permisos';
import type { PropsPantalla } from '../../tipos';
import { AYUDA, CONCEPTO, ESTADO_DISPUTA, PERMISOS, fechaLarga, quedaPara, textoDeError, tipoCobrable } from '../textos';

/**
 * Disputas (4.6): superficie partida (§12e), la lista con el plazo grande (rojo con menos de 48 h) y, al lado, la
 * disputa con «Tienes 20 h 35 min para responder», la tarifa (G287), el aviso temprano y las pruebas: lo que aporta
 * Pagos (recibo y condiciones aceptadas: G286, solo lo de Pagos por ahora), archivos (`uploadDisputeEvidenceFile`) y la
 * explicación; el banco dice el motivo con el texto de la API (G277). Borrador, «Enviar las pruebas» y «Aceptar la
 * disputa», los dos con ventana y sin «Deshacer» (G159); después, la siguiente que vence antes. Filtros sin cifras
 * (G140). En móvil, lista y ficha en RV Panel. `?disputa=` la abre (destino `disputa` de G298).
 */
const FILTROS = [
    { etiqueta: 'Abiertas', estado: 'open' },
    { etiqueta: 'Pruebas enviadas', estado: 'evidence_submitted' },
    { etiqueta: 'Ganadas', estado: 'won' },
    { etiqueta: 'Perdidas', estado: 'lost' },
] as const;

interface Archivo {
    ref: string;
    nombre: string;
    tamano: number;
}

export function Disputas(props: PropsPantalla) {
    const { ops, negocio, movil, membresia, nombreNegocio, navegar, busqueda } = props;
    const permisos = usePermisos();
    const ver = permisos.puede(PERMISOS.pagosVer);
    const responder = permisos.puede(PERMISOS.disputas);
    const [filtro, setFiltro] = useState(0);
    const pag = usePaginacionCursor();
    const { reiniciar } = pag;
    const [cargada, setCargada] = useState<{ vista: string; lista: ListaDisputas | null; error: unknown } | null>(null);
    const [intento, setIntento] = useState(0);
    const [elegida, setElegida] = useState<string | null>(busqueda.get('disputa'));

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    const estado = FILTROS[filtro]?.estado ?? 'open';
    const vista = `${estado}|${pag.cursor ?? ''}`;
    useEffect(() => {
        if (!ver) {
            return;
        }
        let vivo = true;
        ops.listDisputes(negocio, { status: estado, cursor: pag.cursor })
            .then((l) => {
                if (vivo) {
                    setCargada({ vista, lista: l, error: null });
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setCargada((c) => ({ vista, lista: c?.vista === vista ? c.lista : null, error: e }));
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, ver, estado, vista, pag.cursor, intento]);

    useCabecera({ ayuda: ver ? AYUDA.disputas : [], acciones: [] });

    if (!ver) {
        return <EstadoSinPermiso seccion="Disputas" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    }
    const lista = cargada?.vista === vista ? cargada.lista : null;
    const error = cargada?.vista === vista ? cargada.error : null;
    if (error !== null && lista === null) {
        return <EstadoError seccion="las disputas" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={recargar} movil={movil} />;
    }

    // En ordenador, la primera de la lista se abre sola (la que vence antes).
    const actual = elegida ?? (movil ? null : (lista?.data[0]?.id ?? null));
    const ahora = new Date();

    const listaPintada =
        lista === null ? (
            <EstadoCargando seccion="las disputas" />
        ) : lista.data.length === 0 ? (
            <div className="rv-ca-vacia">
                <span className="rv-ca-vacia__baldosa"><Icono nombre="scale" /></span>
                <strong>{estado === 'open' ? 'Ninguna disputa abierta' : 'No hay disputas aquí'}</strong>
                <span>{estado === 'open' ? 'Si el banco de un cliente reclama un cargo, aparece aquí con su plazo para responder.' : 'Prueba con otro filtro.'}</span>
            </div>
        ) : (
            <div className="rv-pg-disputas__lista" role="list" aria-label="Disputas">
                {lista.data.map((d) => {
                    const q = quedaPara(d.evidence_due_by, ahora);
                    const abierta = d.status === 'open';
                    return (
                        <button key={d.id} type="button" role="listitem" aria-current={d.id === actual} className={`rv-pg-disputa${d.id === actual ? ' rv-pg-disputa--on' : ''}`} onClick={() => { setElegida(d.id); }}>
                            <span className={`rv-pg-disputa__plazo${abierta && q.urgente ? ' rv-pg-rojo' : ''}`}>{abierta ? q.corto : <Chip estado={ESTADO_DISPUTA[d.status][1]}>{ESTADO_DISPUTA[d.status][0]}</Chip>}</span>
                            <span className="rv-ca-fila__texto">
                                <strong>{`${tipoCobrable(d.payable.type)[0]} · ${dinero(d.amount)}`}</strong>
                                <span>{d.bank_reason_label ?? d.bank_reason}</span>
                            </span>
                        </button>
                    );
                })}
            </div>
        );

    const ficha = actual === null ? null : (
        <FichaDeDisputa
            key={actual}
            {...props}
            id={actual}
            responder={responder}
            onHecho={() => {
                setElegida(null);
                recargar();
            }}
        />
    );

    return (
        <div className="rv-ca-apartado">
            <Filtros etiqueta="Estado de las disputas" items={FILTROS} valor={filtro} onCambio={(i) => { setFiltro(i); setElegida(null); reiniciar(); }} />
            {!responder && <p className="rv-ca-nota"><Icono nombre="eye" /><span>Responderlas lo hace el propietario o un administrador.</span></p>}
            {movil ? (
                <>
                    {listaPintada}
                    <PanelLateral abierto={actual !== null} onCerrar={() => { setElegida(null); }} movil tamano="ficha" titulo="Disputa" icono="scale">
                        {ficha}
                    </PanelLateral>
                </>
            ) : (
                <div className="rv-pg-disputas">
                    {listaPintada}
                    <div className="rv-pg-disputas__ficha">{ficha ?? <span className="rv-ca-nota rv-ca-nota--gris">Elige una disputa.</span>}</div>
                </div>
            )}
            {lista !== null && <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={haySiguiente(lista.next_cursor)} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(lista.next_cursor); }} movil={movil} />}
        </div>
    );
}

function FichaDeDisputa({ ops, negocio, movil, id, responder, onHecho, navegar, ruta }: PropsPantalla & { id: string; responder: boolean; onHecho: () => void }) {
    const avisar = useAvisoBreve();
    const [d, setD] = useState<FichaDisputa | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [incluir, setIncluir] = useState<Readonly<Record<string, boolean>>>({});
    const [archivos, setArchivos] = useState<Archivo[]>([]);
    const [explicacion, setExplicacion] = useState('');
    const [subiendo, setSubiendo] = useState(false);
    const [ventana, setVentana] = useState<'enviar' | 'aceptar' | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const [intento, setIntento] = useState(0);
    const entrada = useRef<HTMLInputElement>(null);

    useEffect(() => {
        let vivo = true;
        ops.getDispute(negocio, id)
            .then((x) => {
                if (!vivo) {
                    return;
                }
                setD(x);
                setError(null);
                // El borrador guardado: archivos y explicación.
                const guardadas = x.evidence ?? [];
                setArchivos(guardadas.filter((p) => p.file_ref !== undefined).map((p) => ({ ref: p.file_ref ?? '', nombre: p.text ?? 'Archivo', tamano: 0 })));
                setExplicacion(guardadas.find((p) => p.file_ref === undefined && p.kind === 'other')?.text ?? '');
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, id, intento]);

    if (error !== null && d === null) {
        return <EstadoError seccion="la disputa" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={() => { setIntento((i) => i + 1); }} movil={movil} />;
    }
    if (d === null) {
        return <EstadoCargando seccion="la disputa" />;
    }

    const abierta = d.status === 'open';
    const editable = abierta && responder;
    const pago = d.prepared_by_payments?.payment;
    const aporta: { clave: string; prueba: Prueba; titulo: string; linea: string; icono: 'receipt' | 'file-text' }[] = [
        ...(pago === undefined
            ? []
            : [{ clave: 'recibo', prueba: { kind: 'receipt' as const, source_module: 'pagos' }, titulo: 'Recibo del pago', linea: `Pagos · ${dinero(pago.amount)} ${CONCEPTO[pago.concept].toLocaleLowerCase('es')} el ${diaMes(pago.completed_at ?? pago.created_at)}`, icono: 'receipt' as const }]),
        ...(d.prepared_by_payments?.policy_versions ?? []).map((v) => ({
            clave: v.id,
            prueba: { kind: 'policy_accepted' as const, source_module: 'pagos', text: v.conditions_text },
            titulo: 'Condiciones aceptadas',
            linea: `Pagos · versión ${String(v.number)} de la política`,
            icono: 'file-text' as const,
        })),
    ];
    const va = (clave: string) => incluir[clave] ?? true;
    const pruebas = (): Prueba[] => [
        ...aporta.filter((a) => va(a.clave)).map((a) => a.prueba),
        ...archivos.map((a) => ({ kind: 'other' as const, file_ref: a.ref, text: a.nombre })),
        ...(explicacion.trim() === '' ? [] : [{ kind: 'other' as const, text: explicacion.trim() }]),
    ];

    const subir = (archivo: File) => {
        setSubiendo(true);
        ops.uploadDisputeEvidenceFile(negocio, d.id, archivo)
            .then((f) => {
                setArchivos((a) => [...a, { ref: f.file_ref, nombre: f.filename, tamano: f.size_bytes }]);
            })
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? textoDeError(e, 'No he podido subir el archivo. Tiene que ser PDF, JPG o PNG de hasta 5 MB.') });
            })
            .finally(() => {
                setSubiendo(false);
                if (entrada.current !== null) {
                    entrada.current.value = '';
                }
            });
    };
    const guardarBorrador = () => {
        setOcupado(true);
        ops.submitDisputeEvidence(negocio, d.id, pruebas(), false)
            .then((x) => {
                setD(x);
                avisar({ tipo: 'ok', texto: 'Borrador guardado. Nada se envía hasta que pulses «Enviar las pruebas».' });
            })
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? textoDeError(e, 'No he podido guardar el borrador. Prueba de nuevo.') });
            })
            .finally(() => { setOcupado(false); });
    };
    const terminar = (accion: Promise<unknown>, texto: string) => {
        setOcupado(true);
        accion
            .then(() => {
                setOcupado(false);
                setVentana(null);
                avisar({ tipo: 'ok', texto });
                onHecho();
            })
            .catch((e: unknown) => {
                setOcupado(false);
                setVentana(null);
                avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? textoDeError(e, 'No he podido hacerlo. Prueba de nuevo.') });
            });
    };

    const q = quedaPara(d.evidence_due_by);
    const [tipo] = tipoCobrable(d.payable.type);

    return (
        <div className="rv-ca-cuerpo">
            <div className="rv-pg-disputa-cab">
                <span className="rv-ca-baldosa"><Icono nombre="scale" /></span>
                <span className="rv-ca-fila__texto">
                    <strong>{`${tipo} · ${dinero(d.amount)}`}</strong>
                    <span>{pago?.description ?? CONCEPTO[pago?.concept ?? 'sale']}</span>
                </span>
                {pago !== undefined && (
                    <button type="button" className="rv-b rv-b-sec" onClick={() => { navegar(`${ruta}?apartado=pagos&pago=${encodeURIComponent(pago.id)}`); }}>
                        <Icono nombre="receipt" />
                        <span>Ver el pago</span>
                    </button>
                )}
            </div>
            <div className="rv-ca-bloque">
                <span className="rv-pg-meta">{`Disputa · abierta el ${diaMes(d.opened_at)} · ${d.bank_reason_label ?? d.bank_reason}`}</span>
                {abierta ? (
                    <>
                        <h2 className="rv-pg-hero__titulo">
                            Tienes <em>{q.largo}</em> para responder
                        </h2>
                        <p>{`Hasta el ${fechaLarga(d.evidence_due_by)}. Si no respondes antes, la disputa se pierde.`}</p>
                    </>
                ) : (
                    <h2 className="rv-pg-hero__titulo">{d.status === 'evidence_submitted' ? 'Pruebas enviadas: ahora decide el banco' : d.status === 'won' ? 'Ganada: el dinero vuelve a tu cuenta' : 'Perdida: el banco devolvió el dinero al cliente'}</h2>
                )}
                {d.fee != null && <span className="rv-ca-nota rv-ca-nota--gris"><Icono nombre="percent" /><span>{`Tarifa de la disputa en Stripe: ${dinero(d.fee)}.`}</span></span>}
            </div>
            {d.early_warning === true && (
                <div className="rv-ca-caja rv-ca-caja--aviso"><div className="rv-ca-caja__fila"><Icono nombre="triangle-alert" /><span>Aviso temprano: el banco todavía la está revisando. Aún puedes devolver el pago desde Pagos y evitar la disputa.</span></div></div>
            )}
            <div className="rv-ca-bloque">
                <strong>Tus pruebas</strong>
                {aporta.length > 0 && (
                    <details className="rv-pg-plegable" open={!movil}>
                        <summary>
                            <span className="rv-ca-baldosa"><Icono nombre="sparkles" /></span>
                            <span className="rv-ca-fila__texto"><strong>Lo que ya aporta Reservaria</strong><span>Va en el expediente: quita lo que no quieras enviar.</span></span>
                            <Icono nombre="chevron-down" className="rv-pg-plegable__flecha" />
                        </summary>
                        {aporta.map((a) => (
                            <Casilla key={a.clave} marcada={va(a.clave)} bloqueada={!editable} onCambio={(m) => { setIncluir((x) => ({ ...x, [a.clave]: m })); }}>
                                <strong>{a.titulo}</strong>
                                <br />
                                <span className="rv-ca-nota--gris">{a.linea}</span>
                            </Casilla>
                        ))}
                    </details>
                )}
                {archivos.map((a) => (
                    <div key={a.ref} className="rv-ca-elegido">
                        <span className="rv-ca-baldosa"><Icono nombre="paperclip" /></span>
                        <span><strong>{a.nombre}</strong>{a.tamano > 0 && <small>{kilobytes(a.tamano)}</small>}</span>
                        {editable && <button type="button" className="rv-bi rv-ca-fantasma" aria-label={`Quitar ${a.nombre}`} onClick={() => { setArchivos((x) => x.filter((y) => y.ref !== a.ref)); }}><Icono nombre="x" /></button>}
                    </div>
                ))}
                {editable && (
                    <span>
                        <input
                            ref={entrada}
                            type="file"
                            hidden
                            accept="application/pdf,image/jpeg,image/png"
                            aria-label="Añadir fotos o documentos"
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f !== undefined) {
                                    subir(f);
                                }
                            }}
                        />
                        <button type="button" className="rv-b rv-b-sec" disabled={subiendo} onClick={() => { entrada.current?.click(); }}>
                            <Icono nombre={subiendo ? 'loader-circle' : 'upload'} className={subiendo ? 'rv-gira' : undefined} />
                            <span>{subiendo ? 'Subiendo…' : 'Añadir fotos o documentos'}</span>
                        </button>
                    </span>
                )}
                <Campo etiqueta="Explica lo que pasó" valor={explicacion} onChange={setExplicacion} filas={5} max={5000} bloqueado={!editable} ayuda="Qué pasó, con fechas. Lo lee el banco." />
                {editable && <span className="rv-ca-nota rv-ca-nota--gris">Nada se envía hasta que pulses «Enviar las pruebas».</span>}
            </div>
            {editable && (
                <div className="rv-pg-botones">
                    <button type="button" className="rv-b rv-b-pri" onClick={() => { setVentana('enviar'); }}><Icono nombre="send" /><span>Enviar las pruebas</span></button>
                    <button type="button" className="rv-b rv-b-sec" disabled={ocupado} onClick={guardarBorrador}><Icono nombre="check" /><span>Guardar el borrador</span></button>
                    <button type="button" className="rv-b rv-b-sec rv-b-rojo" onClick={() => { setVentana('aceptar'); }}><Icono nombre="flag" /><span>Aceptar la disputa</span></button>
                </div>
            )}
            <Ventana
                abierta={ventana === 'enviar'}
                titulo="¿Enviar las pruebas al banco?"
                texto="Se envían una sola vez: revisa que esté todo. No se puede deshacer."
                accion="Enviar las pruebas"
                accionIcono="send"
                ocupado={ocupado}
                ocupadoTexto="Enviando…"
                movil={movil}
                onAccion={() => { terminar(ops.submitDisputeEvidence(negocio, d.id, pruebas(), true), 'Pruebas enviadas. Ahora decide el banco.'); }}
                onCerrar={() => { setVentana(null); }}
            />
            <Ventana
                abierta={ventana === 'aceptar'}
                titulo="¿Aceptar la disputa?"
                texto={`Das la disputa por perdida: el banco devuelve ${dinero(d.amount)} al cliente. No se puede deshacer.`}
                accion="Aceptar la disputa"
                accionIcono="flag"
                ocupado={ocupado}
                ocupadoTexto="Aceptando…"
                movil={movil}
                onAccion={() => { terminar(ops.acceptDispute(negocio, d.id), 'Disputa aceptada.'); }}
                onCerrar={() => { setVentana(null); }}
            />
        </div>
    );
}

